import * as React from 'react';
import {
  useParams,
  UNSAFE_NavigationContext,
  type Location,
  type NavigateOptions,
  type To,
} from 'react-router';
import {
  useStableLocation as useLocation,
  useStableNavigate,
} from './StableLocationProvider';

export type History = {
  push: (
    to:
      | To
      | { pathname?: string; search?: string; hash?: string; state?: unknown },
  ) => void;
  replace: (
    to:
      | To
      | { pathname?: string; search?: string; hash?: string; state?: unknown },
  ) => void;
  go: (delta: number) => void;
  goBack: () => void;
  goForward: () => void;
  location: Location;
};

export type RouteMatch<Params extends Record<string, string | undefined>> = {
  params: Params;
};

export type RouteComponentProps<
  Params extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
> = {
  history?: History;
  location?: Location;
  match?: RouteMatch<Params>;
};

type PromptMessage = string | ((location: Location) => string | boolean);

/** Asked before an in-app navigation; false keeps the current page. */
const blockers = new Set<(next: Location) => boolean>();

/* BrowserRouter's history in react-router 7 has no block(), so Prompt wraps
   its push / replace (what Link, useNavigate and useHistory end in) once.
   Back / forward and the browser's own navigations are not asked. */
function guardNavigator(navigator: any) {
  if (navigator.__guarded) return;
  navigator.__guarded = true;
  for (const key of ['push', 'replace'] as const) {
    const navigate = navigator[key].bind(navigator);
    navigator[key] = (to: To, ...rest: unknown[]) => {
      const next = navigator.encodeLocation(to) as Location;
      for (const allow of blockers) if (!allow(next)) return;
      navigate(to, ...rest);
    };
  }
}

export function Prompt({
  when,
  message,
}: {
  when: boolean;
  message: PromptMessage;
}) {
  const { navigator } = React.useContext(UNSAFE_NavigationContext) as any;
  const messageRef = React.useRef(message);
  messageRef.current = message;

  React.useEffect(() => {
    if (!when || !navigator) return;
    guardNavigator(navigator);

    const allow = (next: Location) => {
      const msg = messageRef.current;
      const result = typeof msg === 'function' ? msg(next) : msg;
      return typeof result === 'boolean' ? result : window.confirm(result);
    };
    blockers.add(allow);
    return () => {
      blockers.delete(allow);
    };
  }, [navigator, when]);

  React.useEffect(() => {
    if (!when) return;
    if (typeof window === 'undefined') return;

    // a reload or a closed tab has no next location to ask the message about
    const handler = (e: BeforeUnloadEvent) => {
      const msg = messageRef.current;
      e.preventDefault();
      e.returnValue = typeof msg === 'string' ? msg : '';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  return null;
}

function resolveTo(
  input:
    | To
    | { pathname?: string; search?: string; hash?: string; state?: unknown },
  location: Location,
): { to: To; options: NavigateOptions } {
  if (typeof input === 'string') {
    return { to: input, options: {} };
  }

  const pathname =
    'pathname' in input && input.pathname != null
      ? input.pathname
      : location.pathname;

  const rawSearch = (input as any).search;
  const rawHash = (input as any).hash;

  const search =
    rawSearch === undefined
      ? location.search
      : rawSearch === ''
        ? ''
        : rawSearch.startsWith('?')
          ? rawSearch
          : `?${rawSearch}`;

  const hash =
    rawHash === undefined
      ? location.hash
      : rawHash === ''
        ? ''
        : rawHash.startsWith('#')
          ? rawHash
          : `#${rawHash}`;
  const state = (input as any).state;

  const toString = `${pathname}${search || ''}${hash || ''}`;
  return {
    to: toString,
    options: state === undefined ? {} : { state },
  };
}

export function useHistory(): History {
  const navigate = useStableNavigate();
  const location = useLocation();
  const locationRef = React.useRef(location);
  locationRef.current = location;

  return React.useMemo<History>(() => {
    return {
      push: (to) => {
        const { to: resolvedTo, options } = resolveTo(
          to as any,
          locationRef.current,
        );
        navigate(resolvedTo, options);
      },
      replace: (to) => {
        const { to: resolvedTo, options } = resolveTo(
          to as any,
          locationRef.current,
        );
        navigate(resolvedTo, { ...options, replace: true });
      },
      go: (delta) => navigate(delta),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      get location() {
        return locationRef.current;
      },
    };
  }, [navigate]);
}

export function withRouter<P extends object>(
  Component: React.ComponentType<P & RouteComponentProps<any>>,
): React.ComponentType<P> {
  function WithRouter(props: P) {
    const params = useParams();
    const location = useLocation();
    const history = useHistory();

    return (
      <Component
        {...(props as any)}
        history={history}
        location={location}
        match={{ params } as any}
      />
    );
  }

  WithRouter.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return WithRouter;
}
